import { Box, capitalize } from "@mui/material";
import { useDataGrid } from "hooks/useDataGrid";
import { TEXAS_SALES_TAX_IN_PERCENTS } from "models/constants";
import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "store/globalContext";
import { CommonOutletTemplate } from "UI/CommonOutletTemplate";
import { DataGrid } from "UI/DataGrid";
import { TextFieldWithEllipsis } from "UI/TextFiledWithEllipsis";
import { USDollar } from "utils/helpers";
// import { AddNewInvoiceDialog } from "./AddNewInvoiceDialog";

const columns = [
  {
    label: "Date",
    field: "invoice_date",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ created }) => {
      const isUnixTime = typeof created === "number";
      const date = new Date(isUnixTime ? created * 1000 : created);
      return new Intl.DateTimeFormat("en-US", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(date);
    },
  },
  {
    label: "Invoice #",
    field: "invoiceId",
    xs: 1,
    sm: 1,
    md: 1,
    align: "center",
    renderer: ({ invoiceId }) => <TextFieldWithEllipsis>{invoiceId}</TextFieldWithEllipsis>,
  },
  {
    label: "Recipient",
    field: "name",
    xs: 4,
    sm: 4,
    md: 4,
    renderer: ({ name }) => (
      <TextFieldWithEllipsis>{name}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Phone",
    field: "phone",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ phone }) => (
      <TextFieldWithEllipsis>{phone}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Amount",
    field: "amount",
    xs: 2,
    sm: 2,
    md: 2,
    align: "center",
    renderer: ({ amount }) => (
      <Box sx={{ marginRight: "30%", textAlign: "right" }}>
        {USDollar.format(
          amount / 100
        )}
      </Box>
    ),
  },
  {
    label: "Payment",
    field: "status",
    xs: 1,
    sm: 1,
    md: 1,
    align: "center",
    renderer: ({ status }) =>
      status === "succeeded" ? (
        <Box sx={{ color: "green" }}>{capitalize(status)}</Box>
      ) : (
        <Box sx={{ color: "red" }}>{capitalize(status ?? "error")}</Box>
      ),
  },
];

const mobileColumns = [
  {
    label: "Date",
    field: "created",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ created }) => {
      const isUnixTime = typeof created === "number";
      const date = new Date(isUnixTime ? created * 1000 : created);
      return new Intl.DateTimeFormat("en-US").format(date);
    },
  },
  {
    label: "Invoice #",
    field: "invoiceId",
    xs: 2,
    sm: 2,
    md: 2,
    align: "center",
    renderer: ({ invoiceId }) => <TextFieldWithEllipsis>{invoiceId}</TextFieldWithEllipsis>,
  },
  {
    label: "Amount",
    field: "amount",
    xs: 3,
    sm: 3,
    md: 3,
    align: "center",
    renderer: ({ amount }) => (
      <Box sx={{ marginRight: "30%", textAlign: "right" }}>
        {USDollar.format(
          amount / 100
        )}
      </Box>
    ),
  },
  {
    label: "Recipient",
    field: "name",
    xs: 3,
    sm: 3,
    md: 3,
    renderer: ({ name }) => (
      <TextFieldWithEllipsis>{name}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Payment",
    field: "status",
    xs: 2,
    sm: 2,
    md: 2,
    align: "center",
    renderer: ({ status }) =>
      status === "succeeded" ? (
        <Box sx={{ color: "green" }}>{capitalize(status)}</Box>
      ) : (
        <Box sx={{ color: "red" }}>{capitalize(status ?? "error")}</Box>
      ),
  },
];

const gerRecipientColumns = (taxesScale) => [
  {
    label: "Date",
    field: "created",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ created }) => {
      const isUnixTime = typeof created === "number";
      const date = new Date(isUnixTime ? created * 1000 : created);
      return new Intl.DateTimeFormat("en-US", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(date);
    },
  },
  {
    label: "Invoice #",
    field: "invoiceId",
    xs: 3,
    sm: 3,
    md: 3,
    align: "center",
    renderer: ({ invoiceId }) => <TextFieldWithEllipsis>{invoiceId}</TextFieldWithEllipsis>,
  },
  {
    label: "Amount",
    field: "amount",
    xs: 4,
    sm: 4,
    md: 4,
    align: "center",
    renderer: ({ amount }) => (
      <Box sx={{ marginRight: "30%", textAlign: "right" }}>
        {USDollar.format(
          amount / 100
        )}
      </Box>
    ),
  },
  {
    label: "Status",
    field: "paymentStatus",
    xs: 3,
    sm: 3,
    md: 3,
    align: "center",
    renderer: ({ status }) =>
      status === "succeeded" ? (
        <Box sx={{ color: "green" }}>{capitalize(status)}</Box>
      ) : (
        <Box sx={{ color: "red" }}>{capitalize(status ?? "error")}</Box>
      ),
  },
];

const gerRecipientMobileColumns = (taxesScale) => [
  {
    label: "Date",
    field: "created",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ created }) => {
      const isUnixTime = typeof created === "number";
      const date = new Date(isUnixTime ? created * 1000 : created);
      return new Intl.DateTimeFormat("en-US", {
        dateStyle: "short",
      }).format(date);
    },
  },
  {
    label: "Invoice #",
    field: "invoiceId",
    xs: 3,
    sm: 3,
    md: 3,
    align: "center",
    renderer: ({ invoiceId }) => <TextFieldWithEllipsis>{invoiceId}</TextFieldWithEllipsis>,
  },
  {
    label: "Amount",
    field: "amount",
    xs: 4,
    sm: 4,
    md: 4,
    align: "center",
    renderer: ({ amount }) =>       <Box sx={{ marginRight: "30%", textAlign: "right" }}>
    {USDollar.format(
      amount / 100
    )}
  </Box>,
  },
  {
    label: "Status",
    field: "paymentStatus",
    xs: 3,
    sm: 3,
    md: 3,
    align: "center",
    renderer: ({ paymentStatus }) =>
      paymentStatus === "Paid" ? (
        <Box sx={{ color: "green" }}>Paid</Box>
      ) : (
        <Box sx={{ color: "red" }}>Required</Box>
      ),
  },
];

export const Payments = () => {
  const navigate = useNavigate();
  const { userData } = useContext(GlobalContext);
  // const [isNewInvoiceOpen, setIsNewInvoiceOpen] = useState(null);

  const isTexas = userData?.organization?.state === "TX";
  const taxesScale = isTexas ? (100 + TEXAS_SALES_TAX_IN_PERCENTS) / 100 : 1;

  const dataGrid = useDataGrid({
    url: "/payment/find",
    defaultSorter: {
      name: "created",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${userData.cognitoId}-payments`,
    additionalParams: useMemo(
      () => ({
        recipientId: userData?.organization?.id ?? null,
      }),
      [userData?.organization?.id]
    ),
    restApi: "restApiV2",
  });

  // const handleRowClick = ({ id }) => null;
  const handleRowClick = ({ invoiceId }) => navigate(`/invoice/${invoiceId}`);


  const isSuperAdmin = userData?.role === "Super Admin" || userData?.email === "admin@referral-stream.com";

  const recipientColumns = useMemo(
    () => gerRecipientColumns(taxesScale),
    [taxesScale]
  );
  const recipientMobileColumns = useMemo(
    () => gerRecipientMobileColumns(taxesScale),
    [taxesScale]
  );

  return (
    <CommonOutletTemplate title="Payments">
      <DataGrid
        columns={isSuperAdmin ? columns : recipientColumns}
        mobileColumns={isSuperAdmin ? mobileColumns : recipientMobileColumns}
        handleRowClick={handleRowClick}
        {...dataGrid}
      />
      {/* {isSuperAdmin && (
        <AddNewInvoiceDialog
          open={!!isNewInvoiceOpen}
          handleClose={() => setIsNewInvoiceOpen(false)}
          refreshList={() => dataGrid?.loadData()}
        />
      )} */}
    </CommonOutletTemplate>
  );
};
